import IdentityManager from '@arcgis/core/identity/IdentityManager';
import type { SelectedLayerForAI } from './prefetchLayerFeaturesForAI';

function layerUrl(layer: __esri.Layer): string | undefined {
  if ('url' in layer && typeof (layer as { url?: string }).url === 'string') {
    return (layer as { url: string }).url;
  }
  return undefined;
}

/** Ensure FeatureServer/MapServer roots include layer index 0 for REST queries. */
export function ensureArcGisLayerUrl(serviceUrl: string): string {
  const trimmed = serviceUrl.trim().replace(/\/$/, '');
  if (/\/(FeatureServer|MapServer)$/i.test(trimmed)) {
    return `${trimmed}/0`;
  }
  return trimmed;
}

/**
 * Resolve a short-lived ArcGIS user token for server-side FeatureServer
 * queries. Only uses credentials registered for the layer/service URL — do not
 * fall back to an unrelated portal credential (those often fail server-side as
 * Invalid Token). Never log the token value.
 */
export function findArcGisUserToken(
  serviceUrl?: string | null
): string | undefined {
  if (!serviceUrl || typeof serviceUrl !== 'string') return undefined;

  const candidates = [
    serviceUrl,
    serviceUrl.replace(/\/$/, ''),
    serviceUrl.replace(/\/\d+\/?$/, ''),
    serviceUrl.replace(/\/(FeatureServer|MapServer)\/?\d*\/?$/i, '/$1'),
    serviceUrl.replace(/\/(FeatureServer|MapServer)\/.*$/i, '/$1'),
  ];

  for (const url of candidates) {
    try {
      const cred = IdentityManager.findCredential(url);
      if (cred?.token) return String(cred.token);
    } catch {
      /* try next candidate */
    }
  }

  return undefined;
}

/**
 * Attach ephemeral arcgisUserToken + map FeatureLayer URL so COMS can query
 * userAuth services without shipping 50k+ feature snapshots. Drops oversized
 * sample snapshots when a token is available.
 */
export function withArcGisUserTokensForAI(
  layers: SelectedLayerForAI[],
  options?: {
    getAddedExternalLayers?: (layerId: string) => __esri.Layer[] | undefined;
  }
): SelectedLayerForAI[] {
  return layers.map((layer) => {
    if (layer.kind !== 'external') return layer;
    const mapLayers = options?.getAddedExternalLayers?.(layer.id) ?? [];
    const mapUrls = mapLayers
      .map(layerUrl)
      .filter((u): u is string => Boolean(u));
    // Prefer the live map layer URL; normalize FeatureServer roots to /0.
    const rawUrl = mapUrls[0] || layer.serviceUrl || undefined;
    const queryUrl = rawUrl ? ensureArcGisLayerUrl(rawUrl) : undefined;
    const token =
      (queryUrl ? findArcGisUserToken(queryUrl) : undefined) ||
      findArcGisUserToken(layer.serviceUrl) ||
      mapUrls.map((u) => findArcGisUserToken(u)).find(Boolean);

    const dropSampleSnapshot =
      layer.featureSnapshotCoverage === 'sample' &&
      Array.isArray(layer.featureSnapshot) &&
      layer.featureSnapshot.length > 0;

    let next: SelectedLayerForAI = {
      ...layer,
      ...(queryUrl ? { serviceUrl: queryUrl } : {}),
      ...(token ? { arcgisUserToken: token } : {}),
    };

    if (token && dropSampleSnapshot) {
      const { featureSnapshot: _s, ...rest } = next;
      next = { ...rest, featureSnapshotCoverage: 'sample' };
    }
    return next;
  });
}
